import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../pages/AuthContext';
import "../styles/Purchases.css";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faShoppingCart,
    faUser,
    faPhone,
    faMapMarkerAlt,
    faCalendarAlt,
    faFileInvoice,
    faBox,
    faRupeeSign,
    faInfoCircle,
    faTag,
    faBalanceScale,
    faDollarSign,
    faCalculator,
    faExclamationTriangle,
    faPlus,
    faMinus,
    faTrash,
    faSave,
    faTimes,
    faSpinner,
    faCheckCircle,
    faExclamationCircle,
    faSearch,
    faBarcode,
    faQrcode,
    faCreditCard,
    faMoneyBill,
    faUniversity,
    faWallet,
    faPercent,
    faReceipt,
    faChevronDown,
    faChevronUp,
    faBoxes,
    faHistory,
    faUndo,
    faRedo,
    faPrint,
    faFilePdf,
    faDownload,
    faUpload,
    faEdit,
    faChartLine,
    faTimesCircle,
    faShoppingBag,
    faAddressCard,
    faIdCard,
    faKey,
    faLock,
    faUnlock,
    faEye,
    faEyeSlash,
    faStore,
    faEnvelope,
    faGlobe,
    faBuilding,
    faTruck,
    faShip,
    faPlane,
    faTrain,
    faRoad,
    faMapMarkedAlt,
    faFlag,
    faClock,
    faHourglassHalf,
    faCheckDouble,
    faCamera,
    faCloudUploadAlt
} from '@fortawesome/free-solid-svg-icons';

// ==============================================
// Translation Utility
// ==============================================
const createContainerTranslator = () => {
    let cache = new Map();
    let preloadedLanguages = new Set(['en', 'ur', 'ar', 'es']);
    let translationListeners = new Set();

    const loadFromCache = () => {
        try {
            const cached = localStorage.getItem('ourzest_container_translations');
            if (cached) {
                const parsed = JSON.parse(cached);
                Object.entries(parsed).forEach(([key, value]) => {
                    cache.set(key, value);
                });
            }
        } catch (e) {
            console.log("No cache found in Container Form");
        }
    };

    const saveToCache = () => {
        try {
            const cacheObj = {};
            cache.forEach((value, key) => {
                cacheObj[key] = value;
            });
            localStorage.setItem('ourzest_container_translations', JSON.stringify(cacheObj));
        } catch (e) {
            console.error("Error saving to cache:", e);
        }
    };

    const translateAPI = async (text, targetLang) => {
        if (targetLang === 'en') return text;

        try {
            const response = await fetch('https://translate.argosopentech.com/translate', {
                method: 'POST',
                body: JSON.stringify({ q: text, source: 'en', target: targetLang }),
                headers: { 'Content-Type': 'application/json' }
            });

            if (response.ok) {
                const data = await response.json();
                return data.translatedText;
            }
        } catch (e) {
            console.log("LibreTranslate failed, trying MyMemory");
        }

        try {
            const response = await fetch(
                `https://api.mymemory.translated.net/get?q=${encodeURIComponent(text)}&langpair=en|${targetLang}`
            );
            if (response.ok) {
                const data = await response.json();
                return data.responseData.translatedText;
            }
        } catch (e) {
            console.log("MyMemory translation failed");
        }

        return text;
    };

    const translateText = async (text, targetLang) => {
        const cacheKey = `${text}_${targetLang}`;

        if (cache.has(cacheKey)) {
            return cache.get(cacheKey);
        }

        const translated = await translateAPI(text, targetLang);
        cache.set(cacheKey, translated);
        saveToCache();

        return translated;
    };

    const defaultTexts = {
        newContainer: 'Add Container',
        editContainer: 'Edit Container',
        containerDetails: 'Container Details',
        containerNumber: 'Container No',
        containerType: 'Container Type',
        vehicleNumber: 'Vehicle No',
        driverName: 'Driver Name',
        origin: 'Origin',
        destination: 'Destination',
        dispatchDate: 'Dispatch Date',
        arrivalDate: 'Arrival Date',
        status: 'Status',
        remarks: 'Remarks',
        pending: 'Pending',
        inTransit: 'In Transit',
        arrived: 'Arrived',
        delayed: 'Delayed',
        completed: 'Completed',
        selectStatus: 'Select Status',
        save: 'Save Container',
        update: 'Update Container',
        clear: 'Clear',
        saving: 'Saving...',
        loading: 'Loading...',
        containerAdded: 'Container added successfully!',
        containerUpdated: 'Container updated successfully!',
        containerDeleted: 'Container deleted successfully!',
        failedSave: 'Failed to save container',
        failedUpdate: 'Failed to update container',
        failedDelete: 'Failed to delete container',
        networkError: 'Network error occurred',
        duplicateSubmission: 'Duplicate submission detected. Please wait.',
        noShopSelected: 'No shop selected. Please select a shop first.',
        containerNoRequired: 'Container number is required',
        containerNoExists: 'Container number already exists',
        invalidDate: 'Please enter a valid date',
        arrivalBeforeDispatch: 'Arrival date cannot be before dispatch date',
        success: 'Success',
        error: 'Error',
        warning: 'Warning',
        totalContainers: 'Total Containers',
        containersByStatus: 'Containers by Status',
        recentContainers: 'Recent Containers',
        addNew: 'Add New Container',
        edit: 'Edit',
        delete: 'Delete',
        confirmDelete: 'Are you sure you want to delete this container?',
        cancel: 'Cancel',
        ok: 'OK',
        searchPlaceholder: 'Search by container no, vehicle, driver...',
        filterByStatus: 'Filter by Status',
        filterByDate: 'Filter by Date',
        startDate: 'Start Date',
        endDate: 'End Date',
        applyFilters: 'Apply Filters',
        resetFilters: 'Reset Filters',
        loadingData: 'Loading containers...',
        noContainers: 'No containers found',
        poweredBy: 'Powered by OurZest',
        footer: '© auto year ourzest pvt ltd',
        conveyancePicture: 'Conveyance Picture',
        uploadPicture: 'Upload Picture',
        clickToUpload: 'Click to upload conveyance picture',
        supportedFormats: 'Supports: JPG, PNG, WEBP (Max 5MB)',
        changePicture: 'Change Picture'
    };

    const getTranslations = async (lang) => {
        if (lang === 'en') {
            return defaultTexts;
        }

        const translations = {};
        const promises = [];

        Object.keys(defaultTexts).forEach(key => {
            const cacheKey = `${defaultTexts[key]}_${lang}`;
            if (cache.has(cacheKey)) {
                translations[key] = cache.get(cacheKey);
            } else {
                promises.push(
                    translateText(defaultTexts[key], lang).then(translated => {
                        translations[key] = translated;
                    })
                );
            }
        });

        if (promises.length > 0) {
            await Promise.all(promises);
            saveToCache();
        }

        return translations;
    };

    const addListener = (callback) => {
        translationListeners.add(callback);
    };

    const removeListener = (callback) => {
        translationListeners.delete(callback);
    };

    const notifyListeners = (lang) => {
        translationListeners.forEach(callback => callback(lang));
    };

    const checkLanguageChange = () => {
        const currentLang = localStorage.getItem('ourzest_language') || 'en';
        notifyListeners(currentLang);
    };

    const startLanguageCheck = () => {
        checkLanguageChange();
        const intervalId = setInterval(checkLanguageChange, 500);
        window.addEventListener('storage', checkLanguageChange);
        return () => {
            clearInterval(intervalId);
            window.removeEventListener('storage', checkLanguageChange);
        };
    };

    loadFromCache();

    return {
        getTranslations,
        translateText,
        addListener,
        removeListener,
        startLanguageCheck
    };
};

const containerTranslator = createContainerTranslator();

// ==============================================
// Toast Notification Component
// ==============================================
const ToastNotification = ({ toast, setToast, translations }) => {
    const getIconForType = (type) => {
        switch (type) {
            case 'success':
                return faCheckCircle;
            case 'error':
                return faExclamationCircle;
            case 'warning':
                return faExclamationTriangle;
            default:
                return faCheckCircle;
        }
    };

    const getTitleForType = (type) => {
        switch (type) {
            case 'success':
                return translations?.success || 'Success';
            case 'error':
                return translations?.error || 'Error';
            case 'warning':
                return translations?.warning || 'Warning';
            default:
                return '';
        }
    };

    if (!toast.show) return null;

    const opacity = Math.max(toast.progress / 100, 0.1);

    return (
        <div
            className={`toast-notification ${toast.type} ${toast.direction === 'rtl' ? 'toast-rtl' : 'toast-ltr'}`}
            style={{ opacity }}
        >
            <div className="toast-content">
                <div className="toast-icon">
                    <FontAwesomeIcon icon={getIconForType(toast.type)} />
                </div>
                <div className="toast-text">
                    <div className="toast-title">
                        {getTitleForType(toast.type)}
                    </div>
                    <div className="toast-message">
                        {toast.message}
                    </div>
                </div>
                <button
                    className="toast-close"
                    onClick={() => setToast(prev => ({ ...prev, show: false }))}
                >
                    <FontAwesomeIcon icon={faTimes} />
                </button>
            </div>
            <div className="toast-timer">
                <div
                    className="toast-progress-bar"
                    style={{
                        width: `${toast.progress}%`,
                        transition: 'width 0.1s linear'
                    }}
                ></div>
                <div className="toast-countdown">
                    {toast.timeLeft}s
                </div>
            </div>
        </div>
    );
};

// ==============================================
// Business Info Component
// ==============================================
const BusinessInfo = ({ businessInfo, translations }) => {
    if (!businessInfo) return null;

    return (
        <div className="business-info-card" style={{ 
            background: 'linear-gradient(135deg, #f8fafc, #f1f5f9)',
            borderRadius: '12px',
            padding: '15px',
            marginBottom: '20px',
            display: 'flex',
            alignItems: 'center',
            gap: '15px'
        }}>
            {businessInfo.bussinesslogo && (
                <img 
                    src={businessInfo.bussinesslogo} 
                    alt="Logo" 
                    style={{ 
                        width: '50px', 
                        height: '50px', 
                        objectFit: 'contain',
                        borderRadius: '8px'
                    }}
                />
            )}
            <div className="business-details">
                <div style={{ fontSize: '16px', fontWeight: '700', color: '#0f172a' }}>
                    {businessInfo.businessname}
                </div>
                {businessInfo.businessaddress && (
                    <div style={{ fontSize: '12px', color: '#64748b', marginTop: '2px' }}>
                        <FontAwesomeIcon icon={faMapMarkerAlt} size="xs" style={{ marginRight: '5px' }} />
                        {businessInfo.businessaddress}
                    </div>
                )}
                {businessInfo.phonenumber && (
                    <div style={{ fontSize: '12px', color: '#64748b', marginTop: '2px' }}>
                        <FontAwesomeIcon icon={faPhone} size="xs" style={{ marginRight: '5px' }} />
                        {businessInfo.phonenumber}
                    </div>
                )}
            </div>
        </div>
    );
};

// ==============================================
// Main Container Component
// ==============================================
const AddContainers = ({ containerId = null, onSuccess = null }) => {
    const { user, currentShop } = useAuth();

    const [shopId, setShopId] = useState(() => {
        if (user?.shop_id) {
            return user.shop_id;
        }
        return localStorage.getItem('current_shop_id') || '';
    });

    const [shopInfo, setShopInfo] = useState({
        id: shopId,
        name: currentShop?.shop_name || localStorage.getItem('current_shop_name') || ''
    });

    const [businessInfo, setBusinessInfo] = useState(null);
    const [summary, setSummary] = useState(null);

    // Picture states - now for single save operation
    const [pictureFile, setPictureFile] = useState(null);
    const [picturePreview, setPicturePreview] = useState(null);
    const [existingPictureUrl, setExistingPictureUrl] = useState('');
    const fileInputRef = useRef(null);

    const [formData, setFormData] = useState({
        container_no: '',
        container_type: '',
        vehicle_no: '',
        driver_name: '',
        origin: '',
        destination: '',
        dispatch_date: '',
        arrival_date: '',
        status: 'pending',
        remarks: '',
        themeColor: localStorage.getItem('ourzest_theme') || '#8b5cf6'
    });

    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [isEditMode, setIsEditMode] = useState(false);
    const [errors, setErrors] = useState({});
    const [currentLanguage, setCurrentLanguage] = useState(() => {
        return localStorage.getItem('ourzest_language') || 'en';
    });
    const [translations, setTranslations] = useState({});
    const [isTranslating, setIsTranslating] = useState(false);

    // Refs
    const toastTimerRef = useRef(null);

    // Toast state
    const [toast, setToast] = useState({
        show: false,
        type: '',
        message: '',
        direction: 'ltr',
        progress: 100,
        timeLeft: 3
    });

    // Status options
    const statusOptions = [
        { value: 'pending', label: 'pending', icon: faClock },
        { value: 'in_transit', label: 'inTransit', icon: faTruck },
        { value: 'arrived', label: 'arrived', icon: faCheckDouble },
        { value: 'delayed', label: 'delayed', icon: faHourglassHalf },
        { value: 'completed', label: 'completed', icon: faCheckCircle }
    ];

    // Container type options
    const containerTypeOptions = [
        { value: '20ft', label: '20ft' },
        { value: '40ft', label: '40ft' },
        { value: '40ft HC', label: '40ft HC' },
        { value: '45ft', label: '45ft' },
        { value: '20ft Reefer', label: '20ft Reefer' },
        { value: '40ft Reefer', label: '40ft Reefer' },
        { value: '20ft Open Top', label: '20ft Open Top' },
        { value: '40ft Open Top', label: '40ft Open Top' },
        { value: '20ft Flat Rack', label: '20ft Flat Rack' },
        { value: '40ft Flat Rack', label: '40ft Flat Rack' },
        { value: 'Tank Container', label: 'Tank Container' },
        { value: 'Other', label: 'Other' }
    ];

    // Show toast function
    const showToast = useCallback((type, message) => {
        if (toastTimerRef.current) {
            clearInterval(toastTimerRef.current);
        }

        const isRTL = currentLanguage === 'ur' || currentLanguage === 'ar';
        const direction = isRTL ? 'rtl' : 'ltr';

        setToast({
            show: true,
            type,
            message,
            direction,
            progress: 100,
            timeLeft: 3
        });

        let timeLeft = 3;
        const timer = setInterval(() => {
            timeLeft -= 0.1;
            const progress = (timeLeft / 3) * 100;

            setToast(prev => ({
                ...prev,
                progress,
                timeLeft: parseFloat(timeLeft.toFixed(1))
            }));

            if (timeLeft <= 0) {
                clearInterval(timer);
                setToast(prev => ({ ...prev, show: false }));
            }
        }, 100);

        toastTimerRef.current = timer;
    }, [currentLanguage]);

    // Clean up timer
    useEffect(() => {
        return () => {
            if (toastTimerRef.current) {
                clearInterval(toastTimerRef.current);
            }
        };
    }, []);

    // Load translations
    const loadTranslations = useCallback(async (lang) => {
        if (!lang) return;

        setIsTranslating(true);
        try {
            const trans = await containerTranslator.getTranslations(lang);
            setTranslations(trans);

            const isRTL = lang === 'ur' || lang === 'ar';
            document.documentElement.dir = isRTL ? 'rtl' : 'ltr';
            document.body.dir = isRTL ? 'rtl' : 'ltr';

        } catch (error) {
            console.error('Error loading translations:', error);
        } finally {
            setIsTranslating(false);
        }
    }, []);

    // Setup language change listener
    useEffect(() => {
        loadTranslations(currentLanguage);

        const handleLanguageChange = () => {
            const newLang = localStorage.getItem('ourzest_language') || 'en';
            if (newLang !== currentLanguage) {
                setCurrentLanguage(newLang);
                loadTranslations(newLang);
            }
        };

        window.addEventListener('storage', handleLanguageChange);

        const intervalId = setInterval(() => {
            const storedLang = localStorage.getItem('ourzest_language') || 'en';
            if (storedLang !== currentLanguage) {
                setCurrentLanguage(storedLang);
                loadTranslations(storedLang);
            }
        }, 500);

        const translationListener = (lang) => {
            if (lang !== currentLanguage) {
                setCurrentLanguage(lang);
                loadTranslations(lang);
            }
        };

        containerTranslator.addListener(translationListener);

        return () => {
            window.removeEventListener('storage', handleLanguageChange);
            clearInterval(intervalId);
            containerTranslator.removeListener(translationListener);
        };
    }, [currentLanguage, loadTranslations]);

    // Load business info
    const loadBusinessInfo = useCallback(async () => {
        if (!user?.username) return;

        try {
            const response = await fetch('https://ourzestapp.online/oz_backend/get_business_info.php', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    action: 'get_business_info',
                    username: user.username
                })
            });

            const data = await response.json();
            if (data.status === 'success') {
                setBusinessInfo(data.data);
            }
        } catch (error) {
            console.error('Error loading business info:', error);
        }
    }, [user?.username]);

    // Load summary
    const loadSummary = useCallback(async () => {
        if (!shopId) return;

        try {
            const response = await fetch('https://ourzestapp.online/oz_backend/containers.php', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    action: 'get_summary',
                    shop_id: shopId
                })
            });

            const data = await response.json();
            if (data.status === 'success') {
                setSummary(data.data);
            }
        } catch (error) {
            console.error('Error loading summary:', error);
        }
    }, [shopId]);

    // Load container data if in edit mode
    useEffect(() => {
        if (containerId) {
            setIsEditMode(true);
            loadContainerData(containerId);
        }
    }, [containerId]);

    // Load data on mount
    useEffect(() => {
        loadBusinessInfo();
        if (!containerId) {
            loadSummary();
        }
    }, [shopId, containerId, loadBusinessInfo, loadSummary]);

    // Update shopInfo when currentShop changes
    useEffect(() => {
        if (currentShop) {
            setShopInfo({
                id: currentShop.shop_id,
                name: currentShop.shop_name
            });
        }
    }, [currentShop]);

    // Apply theme
    useEffect(() => {
        const applyTheme = (color) => {
            try {
                if (!/^#[0-9A-F]{6}$/i.test(color)) color = '#8b5cf6';

                const hex = color.replace('#', '');
                const r = parseInt(hex.substring(0, 2), 16);
                const g = parseInt(hex.substring(2, 4), 16);
                const b = parseInt(hex.substring(4, 6), 16);

                const adjust = (amount) => {
                    let newR = Math.max(0, Math.min(255, r + amount));
                    let newG = Math.max(0, Math.min(255, g + amount));
                    let newB = Math.max(0, Math.min(255, b + amount));
                    return `#${newR.toString(16).padStart(2, '0')}${newG.toString(16).padStart(2, '0')}${newB.toString(16).padStart(2, '0')}`;
                };

                const palette = {
                    'purchase-primary-color': color,
                    'purchase-primary-dark': adjust(-40),
                    'purchase-primary-light': adjust(40),
                    'purchase-primary-ultralight': adjust(20),
                    'purchase-primary-gradient': `linear-gradient(135deg, ${color}, ${adjust(-20)})`,
                    'purchase-primary-shadow': `${color}20`
                };

                Object.entries(palette).forEach(([key, value]) => {
                    document.documentElement.style.setProperty(`--${key}`, value);
                });

            } catch (err) {
                console.error("Error applying theme:", err);
            }
        };

        applyTheme(formData.themeColor);

        const handleThemeChange = () => {
            const theme = localStorage.getItem('ourzest_theme') || '#8b5cf6';
            if (theme !== formData.themeColor) {
                setFormData(prev => ({ ...prev, themeColor: theme }));
                applyTheme(theme);
            }
        };

        handleThemeChange();
        window.addEventListener('storage', handleThemeChange);
        const interval = setInterval(handleThemeChange, 1000);

        return () => {
            window.removeEventListener('storage', handleThemeChange);
            clearInterval(interval);
        };
    }, [formData.themeColor]);

    // Convert file to base64
    const fileToBase64 = (file) => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.readAsDataURL(file);
            reader.onload = () => resolve(reader.result);
            reader.onerror = (error) => reject(error);
        });
    };

    // Handle picture selection (just stores the file, doesn't upload)
    const handlePictureSelect = (e) => {
        const file = e.target.files[0];
        if (!file) return;
        
        // Validate file type
        const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
        if (!allowedTypes.includes(file.type)) {
            showToast('error', 'Invalid file type. Only JPG, PNG, and WEBP are allowed.');
            return;
        }
        
        // Validate file size (max 5MB)
        if (file.size > 5 * 1024 * 1024) {
            showToast('error', 'File size too large. Maximum 5MB allowed.');
            return;
        }
        
        // Create preview
        const reader = new FileReader();
        reader.onloadend = () => {
            setPicturePreview(reader.result);
        };
        reader.readAsDataURL(file);
        
        setPictureFile(file);
    };

    // Remove selected picture
    const handleRemovePicture = () => {
        setPictureFile(null);
        setPicturePreview(null);
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    const loadContainerData = async (id) => {
        if (!shopId) return;

        setLoading(true);
        try {
            const response = await fetch('https://ourzestapp.online/oz_backend/containers.php', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    action: 'get_record',
                    id: id,
                    shop_id: shopId
                })
            });

            const data = await response.json();
            if (data.status === 'success' && data.data) {
                const container = data.data;
                setFormData(prev => ({
                    ...prev,
                    container_no: container.container_no || '',
                    container_type: container.container_type || '',
                    vehicle_no: container.vehicle_no || '',
                    driver_name: container.driver_name || '',
                    origin: container.origin || '',
                    destination: container.destination || '',
                    dispatch_date: container.dispatch_date || '',
                    arrival_date: container.arrival_date || '',
                    status: container.status || 'pending',
                    remarks: container.remarks || ''
                }));
                setExistingPictureUrl(container.conveyance_picture || '');
            } else {
                showToast('error', data.message || 'Failed to load container data');
            }
        } catch (error) {
            showToast('error', translations?.networkError || 'Network error occurred');
        } finally {
            setLoading(false);
        }
    };

    const validateForm = () => {
        const newErrors = {};

        if (!formData.container_no.trim()) {
            newErrors.container_no = translations?.containerNoRequired || 'Container number is required';
        }

        // Validate dates
        if (formData.dispatch_date && formData.arrival_date) {
            const dispatch = new Date(formData.dispatch_date);
            const arrival = new Date(formData.arrival_date);
            if (arrival < dispatch) {
                newErrors.arrival_date = translations?.arrivalBeforeDispatch || 'Arrival date cannot be before dispatch date';
            }
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));

        // Clear error for this field
        if (errors[name]) {
            setErrors(prev => ({ ...prev, [name]: '' }));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!shopId) {
            showToast('error', translations?.noShopSelected || 'No shop selected. Please select a shop first.');
            return;
        }

        if (!validateForm()) {
            const firstError = document.querySelector('.has-error, .error-message-modern');
            if (firstError) {
                firstError.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
            return;
        }

        setSaving(true);

        try {
            const currentUsername = user?.username || '';
            if (!currentUsername) {
                throw new Error("User not authenticated");
            }

            const action = isEditMode ? 'edit' : 'add';
            const endpoint = 'https://ourzestapp.online/oz_backend/containers.php';

            // Prepare payload with form data
            const payload = {
                action: action,
                username: currentUsername,
                shop_id: shopId,
                ...formData
            };

            // If there's a picture selected, convert to base64 and add to payload
            if (pictureFile) {
                const base64Image = await fileToBase64(pictureFile);
                payload.conveyance_picture = base64Image;
            } else if (isEditMode && !pictureFile && !existingPictureUrl) {
                payload.conveyance_picture = null;
            }

            if (isEditMode && containerId) {
                payload.id = containerId;
            }

            const response = await fetch(endpoint, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(payload)
            });

            const data = await response.json();

            if (data.status === 'success') {
                const successMessage = isEditMode
                    ? (translations?.containerUpdated || 'Container updated successfully!')
                    : (translations?.containerAdded || 'Container added successfully!');

                showToast('success', successMessage);

                if (!isEditMode) {
                    handleClear();
                    loadSummary();
                }

                setErrors({});

                if (onSuccess) {
                    onSuccess(data.data?.id || containerId);
                }

            } else {
                showToast('error', data.message || (isEditMode ? translations?.failedUpdate : translations?.failedSave) || 'Failed to save container');
            }
        } catch (error) {
            console.error('Submit error:', error);
            showToast('error', error.message || translations?.networkError || 'Network error occurred');
        } finally {
            setSaving(false);
        }
    };

    const handleClear = () => {
        setFormData({
            container_no: '',
            container_type: '',
            vehicle_no: '',
            driver_name: '',
            origin: '',
            destination: '',
            dispatch_date: '',
            arrival_date: '',
            status: 'pending',
            remarks: '',
            themeColor: formData.themeColor
        });
        setPictureFile(null);
        setPicturePreview(null);
        setExistingPictureUrl('');
        setErrors({});
        setIsEditMode(false);
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    const isRTL = currentLanguage === 'ur' || currentLanguage === 'ar';

    return (
        <div className={`purchases-form-independent ${isRTL ? 'rtl' : ''} ${isTranslating ? 'translating' : ''}`}>
            {/* Toast Notification */}
            <ToastNotification
                toast={toast}
                setToast={setToast}
                translations={translations}
            />

            {/* Modern Form Header */}
            <div className="form-header-modern">
                <div className="header-content">
                    <div className="header-icon">
                        <FontAwesomeIcon icon={isEditMode ? faEdit : faBox} />
                    </div>
                    <div className="header-text">
                        <h1>{isEditMode ? (translations?.editContainer || 'Edit Container') : (translations?.newContainer || 'New Container')}</h1>
                        <p>{translations?.containerDetails || 'Container Details'}</p>
                    </div>
                </div>
                {isTranslating && (
                    <div className="translation-indicator">
                        <FontAwesomeIcon icon={faSpinner} spin />
                        <span>Translating...</span>
                    </div>
                )}
            </div>

            {/* Business Info */}
            <BusinessInfo businessInfo={businessInfo} translations={translations} />

            {/* Modern Form */}
            <form onSubmit={handleSubmit} className="modern-form">
                {/* Container Information Section */}
                <div className="form-section">
                    <div className="section-header">
                        <h3 className="section-title">
                            <FontAwesomeIcon icon={faBox} />
                            {translations?.containerDetails || 'Container Information'}
                        </h3>
                    </div>

                    <div className="form-grid-modern">
                        {/* Container No - Required */}
                        <div className={`form-group-modern ${errors.container_no ? 'has-error' : ''}`}>
                            <div className="input-container">
                                <FontAwesomeIcon icon={faBox} className="input-icon" />
                                <input
                                    type="text"
                                    id="container_no"
                                    name="container_no"
                                    value={formData.container_no}
                                    onChange={handleChange}
                                    placeholder=" "
                                    disabled={saving}
                                    dir={isRTL ? 'rtl' : 'ltr'}
                                    className={errors.container_no ? 'error' : ''}
                                />
                                <label htmlFor="container_no" className="input-label">
                                    {translations?.containerNumber || 'Container No'} <span className="required">*</span>
                                </label>
                                {errors.container_no && (
                                    <div className="error-message">
                                        <FontAwesomeIcon icon={faExclamationTriangle} />
                                        {errors.container_no}
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Container Type */}
                        <div className="form-group-modern">
                            <div className="input-container">
                                <FontAwesomeIcon icon={faTag} className="input-icon" />
                                <select
                                    id="container_type"
                                    name="container_type"
                                    value={formData.container_type}
                                    onChange={handleChange}
                                    disabled={saving}
                                    dir={isRTL ? 'rtl' : 'ltr'}
                                >
                                    <option value="">{translations?.selectStatus || 'Select Type'}</option>
                                    {containerTypeOptions.map(option => (
                                        <option key={option.value} value={option.value}>
                                            {option.label}
                                        </option>
                                    ))}
                                </select>
                                <label htmlFor="container_type" className="input-label">
                                    {translations?.containerType || 'Container Type'}
                                </label>
                            </div>
                        </div>

                        {/* Vehicle No */}
                        <div className="form-group-modern">
                            <div className="input-container">
                                <FontAwesomeIcon icon={faTruck} className="input-icon" />
                                <input
                                    type="text"
                                    id="vehicle_no"
                                    name="vehicle_no"
                                    value={formData.vehicle_no}
                                    onChange={handleChange}
                                    placeholder=" "
                                    disabled={saving}
                                    dir={isRTL ? 'rtl' : 'ltr'}
                                />
                                <label htmlFor="vehicle_no" className="input-label">
                                    {translations?.vehicleNumber || 'Vehicle No'}
                                </label>
                            </div>
                        </div>

                        {/* Driver Name */}
                        <div className="form-group-modern">
                            <div className="input-container">
                                <FontAwesomeIcon icon={faUser} className="input-icon" />
                                <input
                                    type="text"
                                    id="driver_name"
                                    name="driver_name"
                                    value={formData.driver_name}
                                    onChange={handleChange}
                                    placeholder=" "
                                    disabled={saving}
                                    dir={isRTL ? 'rtl' : 'ltr'}
                                />
                                <label htmlFor="driver_name" className="input-label">
                                    {translations?.driverName || 'Driver Name'}
                                </label>
                            </div>
                        </div>

                        {/* Origin */}
                        <div className="form-group-modern">
                            <div className="input-container">
                                <FontAwesomeIcon icon={faMapMarkedAlt} className="input-icon" />
                                <input
                                    type="text"
                                    id="origin"
                                    name="origin"
                                    value={formData.origin}
                                    onChange={handleChange}
                                    placeholder=" "
                                    disabled={saving}
                                    dir={isRTL ? 'rtl' : 'ltr'}
                                />
                                <label htmlFor="origin" className="input-label">
                                    {translations?.origin || 'Origin'}
                                </label>
                            </div>
                        </div>

                        {/* Destination */}
                        <div className="form-group-modern">
                            <div className="input-container">
                                <FontAwesomeIcon icon={faFlag} className="input-icon" />
                                <input
                                    type="text"
                                    id="destination"
                                    name="destination"
                                    value={formData.destination}
                                    onChange={handleChange}
                                    placeholder=" "
                                    disabled={saving}
                                    dir={isRTL ? 'rtl' : 'ltr'}
                                />
                                <label htmlFor="destination" className="input-label">
                                    {translations?.destination || 'Destination'}
                                </label>
                            </div>
                        </div>

                        {/* Dispatch Date */}
                        <div className={`form-group-modern ${errors.dispatch_date ? 'has-error' : ''}`}>
                            <div className="input-container">
                                <FontAwesomeIcon icon={faCalendarAlt} className="input-icon" />
                                <input
                                    type="date"
                                    id="dispatch_date"
                                    name="dispatch_date"
                                    value={formData.dispatch_date}
                                    onChange={handleChange}
                                    disabled={saving}
                                    dir="ltr"
                                    placeholder=" "
                                />
                                <label htmlFor="dispatch_date" className="input-label">
                                    {translations?.dispatchDate || 'Dispatch Date'}
                                </label>
                            </div>
                        </div>

                        {/* Arrival Date */}
                        <div className={`form-group-modern ${errors.arrival_date ? 'has-error' : ''}`}>
                            <div className="input-container">
                                <FontAwesomeIcon icon={faCalendarAlt} className="input-icon" />
                                <input
                                    type="date"
                                    id="arrival_date"
                                    name="arrival_date"
                                    value={formData.arrival_date}
                                    onChange={handleChange}
                                    disabled={saving}
                                    dir="ltr"
                                    placeholder=" "
                                />
                                <label htmlFor="arrival_date" className="input-label">
                                    {translations?.arrivalDate || 'Arrival Date'}
                                </label>
                                {errors.arrival_date && (
                                    <div className="error-message">
                                        <FontAwesomeIcon icon={faExclamationTriangle} />
                                        {errors.arrival_date}
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Status */}
                        <div className="form-group-modern">
                            <div className="input-container">
                                <FontAwesomeIcon icon={faHourglassHalf} className="input-icon" />
                                <select
                                    id="status"
                                    name="status"
                                    value={formData.status}
                                    onChange={handleChange}
                                    disabled={saving}
                                    dir={isRTL ? 'rtl' : 'ltr'}
                                >
                                    {statusOptions.map(option => (
                                        <option key={option.value} value={option.value}>
                                            {translations?.[option.label] || option.value.replace('_', ' ')}
                                        </option>
                                    ))}
                                </select>
                                <label htmlFor="status" className="input-label">
                                    {translations?.status || 'Status'}
                                </label>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Conveyance Picture Upload Section - Single Save */}
                <div className="form-section">
                    <div className="section-header">
                        <h3 className="section-title">
                            <FontAwesomeIcon icon={faCamera} />
                            {translations?.conveyancePicture || 'Conveyance Picture'}
                        </h3>
                    </div>
                    
                    <div className="picture-upload-container" style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '15px',
                        padding: '15px',
                        background: '#f8fafc',
                        borderRadius: '12px',
                        border: '1px solid #e2e8f0'
                    }}>
                        {/* Picture Preview - Show either existing picture or new preview */}
                        {(picturePreview || existingPictureUrl) && (
                            <div className="picture-preview" style={{
                                position: 'relative',
                                display: 'inline-block',
                                maxWidth: '300px'
                            }}>
                                <img 
                                    src={picturePreview || existingPictureUrl} 
                                    alt="Conveyance" 
                                    style={{
                                        width: '100%',
                                        maxHeight: '200px',
                                        objectFit: 'cover',
                                        borderRadius: '8px',
                                        border: '2px solid var(--purchase-primary-color)'
                                    }}
                                />
                                <button 
                                    type="button"
                                    className="delete-picture-btn"
                                    onClick={handleRemovePicture}
                                    disabled={saving}
                                    style={{
                                        position: 'absolute',
                                        top: '-10px',
                                        right: '-10px',
                                        width: '32px',
                                        height: '32px',
                                        borderRadius: '50%',
                                        background: '#ef4444',
                                        color: 'white',
                                        border: 'none',
                                        cursor: 'pointer',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        transition: 'all 0.2s'
                                    }}
                                >
                                    <FontAwesomeIcon icon={faTrash} />
                                </button>
                            </div>
                        )}
                        
                        {/* Upload Area */}
                        <div 
                            className="upload-area" 
                            onClick={() => fileInputRef.current?.click()}
                            style={{
                                border: '2px dashed #cbd5e1',
                                borderRadius: '12px',
                                padding: '30px',
                                textAlign: 'center',
                                cursor: 'pointer',
                                transition: 'all 0.2s',
                                background: 'white'
                            }}
                        >
                            <input
                                type="file"
                                ref={fileInputRef}
                                onChange={handlePictureSelect}
                                accept="image/jpeg,image/jpg,image/png,image/webp"
                                style={{ display: 'none' }}
                                disabled={saving}
                            />
                            <div className="upload-content" style={{ color: '#64748b' }}>
                                <FontAwesomeIcon icon={faCloudUploadAlt} size="2x" style={{ color: 'var(--purchase-primary-color)', marginBottom: '10px' }} />
                                <p style={{ margin: '10px 0 5px', fontSize: '14px' }}>
                                    {picturePreview || existingPictureUrl 
                                        ? (translations?.changePicture || 'Change Picture')
                                        : (translations?.clickToUpload || 'Click to upload conveyance picture')}
                                </p>
                                <small style={{ fontSize: '11px', color: '#94a3b8' }}>{translations?.supportedFormats || 'Supports: JPG, PNG, WEBP (Max 5MB)'}</small>
                            </div>
                        </div>
                        
                        {/* Info text about saving */}
                        <div style={{ fontSize: '12px', color: '#64748b', textAlign: 'center' }}>
                            <FontAwesomeIcon icon={faInfoCircle} style={{ marginRight: '5px' }} />
                            Picture will be saved when you click the Save button
                        </div>
                    </div>
                </div>

                {/* Remarks Section */}
                <div className="form-section">
                    <div className="form-group-modern">
                        <div className="input-container textarea-container">
                            <FontAwesomeIcon icon={faReceipt} className="input-icon" />
                            <textarea
                                id="remarks"
                                name="remarks"
                                value={formData.remarks}
                                onChange={handleChange}
                                placeholder=" "
                                disabled={saving}
                                rows="3"
                                dir={isRTL ? 'rtl' : 'ltr'}
                            ></textarea>
                            <label htmlFor="remarks" className="input-label">
                                {translations?.remarks || 'Remarks'}
                            </label>
                        </div>
                    </div>
                </div>

                {/* Form Actions */}
                <div className="form-actions-modern">
                    <button
                        type="button"
                        className="btn-modern btn-secondary-modern"
                        onClick={handleClear}
                        disabled={saving}
                    >
                        <FontAwesomeIcon icon={faTimes} />
                        <span>{translations?.clear || 'Clear'}</span>
                    </button>
                    <button
                        type="submit"
                        className="btn-modern btn-primary-modern"
                        disabled={saving || isTranslating}
                    >
                        {saving ? (
                            <>
                                <div className="btn-spinner"></div>
                                <span>{translations?.saving || 'Saving...'}</span>
                            </>
                        ) : (
                            <>
                                <FontAwesomeIcon icon={faSave} />
                                <span>{isEditMode ? (translations?.update || 'Update Container') : (translations?.save || 'Save Container')}</span>
                            </>
                        )}
                    </button>
                </div>
            </form>

            {/* Loading Overlay */}
            {loading && (
                <div className="loading-overlay">
                    <div className="loading-content">
                        <div className="loading-spinner"></div>
                        <p>{translations?.loading || 'Loading...'}</p>
                        <p className="loading-subtext">Please wait while we load container data</p>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AddContainers;